import type { JSX } from 'react';
import { useState } from 'react';
import { ActionIcon, Alert, Anchor, Badge, Button, Center, Group, List, Loader, Select, Stack, Table, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, offerAiApi, type AiFinding, type Offer, type OfferAiRun, type OfferAnalysis, type OfferDraft } from '../../api';
import { AI_EFFORTS, AI_MARKS, AI_MODELS, AI_RUN_STATES, AI_STATIONS, ANALYSIS_SECTIONS, CONSTELLATION_LABELS, HOAI_APPLIES, ROMAN_ZONES, formatAmount, formatBytes, formatCount, formatDate, formatDateTime, formatSeconds, formatUsd } from '../../format';
import { HOAI_SERVICES, type HoaiService, hoaiService } from '@pst/shared';
import { FileDrop } from '../../ui/FileDrop';
import { Section } from '../../ui/Section';

const ACCEPT = '.pdf,.zip,.docx,.xlsx,.txt,.md,.csv,.png,.jpg,.jpeg';
const errorText = (e: unknown, fallback: string): string => (e instanceof ApiError ? e.message : fallback);

/**
 * Auswertung (AP-97, ADR 0011): the tender's documents, then the two
 * stations of the office's working rules - the evaluation, checked and
 * approved by a person, and only then the draft of calculation and texts,
 * which a person takes over into a variant. Claude Code on the server does
 * the reading; nothing is taken over without a click.
 */
export function OfferAi({ offer, onChanged }: { offer: Offer; onChanged: () => Promise<void> }): JSX.Element {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['offer-ai-status'], queryFn: offerAiApi.status, refetchInterval: 30_000 });
  const docs = useQuery({ queryKey: ['offer-docs', offer.id], queryFn: () => offerAiApi.documents(offer.id) });
  const runs = useQuery({
    queryKey: ['offer-ai', offer.id],
    queryFn: () => offerAiApi.runs(offer.id),
    refetchInterval: (q) => (q.state.data?.some((r) => r.state === 'neu' || r.state === 'läuft') ? 5000 : false),
  });
  const refreshRuns = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offer-ai', offer.id] });
  const available = status.data?.available === true;
  const running = runs.data?.find((r) => r.state === 'neu' || r.state === 'läuft');
  const analysis = runs.data?.find((r) => r.station === 'analyse');
  const lastGoodAnalysis = runs.data?.find((r) => r.station === 'analyse' && r.state === 'fertig');
  const draft = runs.data?.find((r) => r.station === 'entwurf');

  return (
    <Stack gap="md">
      {status.data && !available && (
        <Alert color="orange" py={6} title="Die KI-Auswertung ist gerade nicht erreichbar">
          {status.data.reason ?? 'Der Grund ist nicht bekannt.'} Unterlagen lassen sich trotzdem ablegen; die Kalkulation geht auch ohne KI.
        </Alert>
      )}
      <Documents offer={offer} count={docs.data?.length ?? 0} docs={docs.data} loading={docs.isPending} />
      <Section
        title="1 · Auswertung der Unterlagen"
        description={`Die KI liest alle Unterlagen und gibt das Leistungssoll, Fristen, Formvorgaben, Nachweise, Wertung, Lücken und Rückfragen zurück – jede Aussage mit Fundstelle, Offenes als [?], Annahmen als [A]. ${status.data ? `${AI_MODELS[status.data.model as keyof typeof AI_MODELS] ?? status.data.model}, Aufwand ${AI_EFFORTS[status.data.effort as keyof typeof AI_EFFORTS] ?? status.data.effort}.` : ''}`}
      >
        <StartAnalysis offer={offer} disabled={!available || !!running || (docs.data?.length ?? 0) === 0} onStarted={refreshRuns} />
        {analysis && <RunHead run={analysis} offerId={offer.id} onRemoved={refreshRuns} />}
        {analysis?.state === 'fertig' && <AnalysisView offer={offer} run={analysis} onApplied={async () => { await refreshRuns(); await onChanged(); }} />}
      </Section>
      <Section
        title="2 · Entwurf von Kalkulation und Texten"
        description="Erst nach Prüfung der Auswertung: Positionen mit Stunden je Rolle und Begründung, Zusatzleistungen, Abgrenzungen, Annahmen, Rückfragen und Risiken für eine Variante. Die Stundensätze setzt das Büro."
      >
        {lastGoodAnalysis
          ? <StartDraft offer={offer} basedOn={lastGoodAnalysis} disabled={!available || !!running} onStarted={refreshRuns} />
          : <Text size="sm" c="dimmed">Zuerst eine Auswertung anfordern und prüfen.</Text>}
        {draft && <RunHead run={draft} offerId={offer.id} onRemoved={refreshRuns} />}
        {draft?.state === 'fertig' && <DraftView offer={offer} run={draft} onApplied={async () => { await refreshRuns(); await onChanged(); }} />}
      </Section>
      {runs.isPending && <Center h={40}><Loader size="sm" /></Center>}
      {runs.data && runs.data.length > 2 && <EarlierRuns offerId={offer.id} runs={runs.data.filter((r) => r !== analysis && r !== draft)} onRemoved={refreshRuns} />}
    </Stack>
  );
}

function Documents({ offer, docs, count, loading }: { offer: Offer; docs: { id: string; fileName: string; bytes: number; createdAt: string }[] | undefined; count: number; loading: boolean }): JSX.Element {
  const queryClient = useQueryClient();
  const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['offer-docs', offer.id] });
  const [text, setText] = useState('');
  const upload = useMutation({
    mutationFn: async (files: File[]) => { for (const f of files) await offerAiApi.addDocument(offer.id, f); },
    onSettled: refresh,
  });
  const addText = useMutation({ mutationFn: () => offerAiApi.addText(offer.id, `Beschreibung-${count + 1}.txt`, text), onSuccess: () => setText(''), onSettled: refresh });
  const remove = useMutation({ mutationFn: (id: string) => offerAiApi.removeDocument(offer.id, id), onSettled: refresh });
  return (
    <Section title="Unterlagen" description="Auslobung, Leistungsbeschreibung, Anlagen – als PDF, ZIP (wird entpackt), Word, Excel, Text oder Bild, bis 40 MB je Datei. Sie bleiben beim Angebot und gehen nur mit einem Auftrag an die KI.">
      {loading && <Center h={40}><Loader size="sm" /></Center>}
      {docs && docs.length > 0 && (
        <Table verticalSpacing={2}>
          <Table.Tbody>
            {docs.map((d) => (
              <Table.Tr key={d.id}>
                <Table.Td><Anchor size="sm" href={offerAiApi.documentUrl(offer.id, d.id)}>{d.fileName}</Anchor></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatBytes(d.bytes)}</Text></Table.Td>
                <Table.Td><Text size="xs" c="dimmed">{formatDateTime(d.createdAt)}</Text></Table.Td>
                <Table.Td w={30}><ActionIcon size="sm" variant="subtle" color="red" aria-label="Unterlage entfernen" title="Unterlage entfernen" onClick={() => { if (window.confirm(`„${d.fileName}“ entfernen?`)) remove.mutate(d.id); }}>×</ActionIcon></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <FileDrop accept={ACCEPT} multiple label={upload.isPending ? 'wird hochgeladen …' : 'Unterlagen hierher ziehen oder auswählen'} hint="PDF, ZIP, Word, Excel, Text, Bild" onFiles={(f) => upload.mutate(f)} disabled={upload.isPending} />
      {upload.isError && <Alert color="red" py={6}>{errorText(upload.error, 'Hochladen fehlgeschlagen.')}</Alert>}
      <Group align="flex-end" gap="xs">
        <Textarea style={{ flex: 1 }} size="xs" label="… oder die Aufgabe als Text beschreiben" autosize minRows={2} maxRows={10} value={text} onChange={(e) => setText(e.currentTarget.value)}
          placeholder="z. B. Anfrage per E-Mail: Machbarkeitsstudie für …, Umfang …, Termin …" />
        <Button size="xs" variant="light" onClick={() => addText.mutate()} loading={addText.isPending} disabled={text.trim().length < 20}>als Unterlage ablegen</Button>
      </Group>
    </Section>
  );
}

function StartAnalysis({ offer, disabled, onStarted }: { offer: Offer; disabled: boolean; onStarted: () => Promise<void> }): JSX.Element {
  const [note, setNote] = useState('');
  const start = useMutation({ mutationFn: () => offerAiApi.analyse(offer.id, note), onSuccess: onStarted });
  return (
    <Stack gap={6}>
      <Text size="xs">
        Vorab festgelegt – bitte prüfen, die Auswertung richtet sich danach:{' '}
        {offer.variants.map((v) => `Variante ${v.code} ${CONSTELLATION_LABELS[v.constellation]}${v.data.partyIds.length ? '' : ' (noch ohne Beteiligte)'}`).join('; ')}.
        Konstellation und Beteiligte werden unter „Kalkulation“ geändert.
      </Text>
      <Group align="flex-end" gap="xs">
        <Textarea style={{ flex: 1 }} size="xs" label="Hinweise zum Auftrag (freiwillig)" autosize minRows={1} maxRows={6} value={note} onChange={(e) => setNote(e.currentTarget.value)}
          placeholder="z. B. Los 2 ist nicht gemeint; Budget laut Telefonat …" />
        <Button onClick={() => start.mutate()} loading={start.isPending} disabled={disabled}>Auswertung anfordern</Button>
      </Group>
      {start.isError && <Alert color="red" py={6}>{errorText(start.error, 'Der Auftrag ließ sich nicht ablegen.')}</Alert>}
    </Stack>
  );
}

function RunHead({ run, offerId, onRemoved }: { run: OfferAiRun; offerId: string; onRemoved: () => Promise<void> }): JSX.Element {
  const s = AI_RUN_STATES[run.state];
  const remove = useMutation({ mutationFn: () => offerAiApi.removeRun(offerId, run.id), onSuccess: onRemoved });
  const m = run.meta ?? {};
  return (
    <Stack gap={4}>
      <Group gap="xs">
        <Badge variant="light" color={s.color}>{s.label}</Badge>
        {(run.state === 'neu' || run.state === 'läuft') && <Loader size="xs" />}
        <Text size="xs" c="dimmed">
          angefordert {formatDateTime(run.createdAt)}
          {m.seconds ? ` · Dauer ${formatSeconds(m.seconds)}` : ''}
          {m.model ? ` · ${m.model}, ${m.effort ?? ''}` : ''}
          {m.files ? ` · ${m.files.length} Dateien gelesen` : ''}
          {m.backend === 'api' && m.costUsd !== undefined ? ` · Claude-API, ca. ${formatUsd(m.costUsd)} (${formatCount((m.inputTokens ?? 0) + (m.outputTokens ?? 0))} Token)` : ''}
          {run.appliedAt ? ` · übernommen ${formatDateTime(run.appliedAt)}` : ''}
        </Text>
        {run.state !== 'neu' && run.state !== 'läuft' && <Anchor size="xs" c="red" component="button" onClick={() => { if (window.confirm('Dieses Ergebnis löschen?')) remove.mutate(); }}>löschen</Anchor>}
      </Group>
      {run.state === 'neu' && <Text size="xs" c="dimmed">Der Auftrag liegt im Auftragsordner; der Server nimmt ihn innerhalb von etwa 20 Sekunden auf. Eine gründliche Auswertung dauert je nach Umfang 5 bis 30 Minuten – die Seite darf solange geschlossen werden.</Text>}
      {run.state === 'fehler' && <Alert color="red" py={6}>{run.error ?? 'Unbekannter Fehler.'}</Alert>}
      {m.skipped && m.skipped.length > 0 && <Text size="xs" c="orange.8">Nicht ausgewertet: {m.skipped.join('; ')}</Text>}
    </Stack>
  );
}

function Mark({ mark }: { mark: AiFinding['mark'] }): JSX.Element | null {
  const m = AI_MARKS[mark];
  return m ? <Badge size="xs" variant="light" color={m.color} title={m.title} mr={4}>{m.label}</Badge> : null;
}

function Source({ source }: { source: string }): JSX.Element | null {
  return source ? <Text span size="xs" c="dimmed"> – {source}</Text> : <Text span size="xs" c="orange.8"> – ohne Fundstelle</Text>;
}

function AnalysisView({ offer, run, onApplied }: { offer: Offer; run: OfferAiRun; onApplied: () => Promise<void> }): JSX.Element {
  const a = run.answer as OfferAnalysis;
  const apply = useMutation({ mutationFn: () => offerAiApi.apply(offer.id, run.id, 'analyse'), onSuccess: onApplied });
  const p = a.project;
  const facts = ([['Vorhaben', p.name], ['Auftraggeber', p.client], ['Ort', p.location], ['Bundesland', p.state], ['Los', p.lot], ['Angebotsfrist', /^\d{4}-\d{2}-\d{2}$/.test(p.dueDate) ? formatDate(p.dueDate) : p.dueDate], ['Bindefrist', p.bindingDays ? `${p.bindingDays} Tage` : ''], ['Bezug', p.reference.replace(/^Bezug:\s*/, '')]] as const).filter(([, v]) => v);
  return (
    <Stack gap="sm">
      <Text size="sm"><b>Umfang:</b> {a.scope}</Text>
      {facts.length > 0 && <Text size="xs">{facts.map(([k, v]) => `${k}: ${v}`).join(' · ')}</Text>}
      <Stack gap={2}>
        <Text size="sm" fw={600}>Leistungssoll je Position</Text>
        <List size="sm" spacing={2} type="ordered">{a.positions.map((x, i) => <List.Item key={i}><b>{x.title}</b>: {x.demand}<Source source={x.source} /></List.Item>)}</List>
      </Stack>
      <Text size="sm"><b>Stunden und Satz je Position ausweisen:</b> {a.showHours}</Text>
      {(Object.keys(ANALYSIS_SECTIONS) as (keyof typeof ANALYSIS_SECTIONS)[]).map((k) => a[k].length > 0 && (
        <Stack key={k} gap={2}>
          <Text size="sm" fw={600}>{ANALYSIS_SECTIONS[k]}</Text>
          <List size="sm" spacing={2}>{a[k].map((f, i) => <List.Item key={i}><Mark mark={f.mark} />{f.text}<Source source={f.source} /></List.Item>)}</List>
        </Stack>
      ))}
      <Stack gap={2}>
        <Text size="sm" fw={600}>Empfehlung zum Zuschnitt</Text>
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{a.recommendation}</Text>
        {a.alternatives.length > 0 && (
          <Table verticalSpacing={2} mt={4}>
            <Table.Thead><Table.Tr><Table.Th>Weg</Table.Th><Table.Th>dafür</Table.Th><Table.Th>dagegen</Table.Th></Table.Tr></Table.Thead>
            <Table.Tbody>{a.alternatives.map((x, i) => <Table.Tr key={i}><Table.Td><Text size="sm" fw={500}>{x.title}</Text></Table.Td><Table.Td><Text size="xs">{x.pros}</Text></Table.Td><Table.Td><Text size="xs">{x.cons}</Text></Table.Td></Table.Tr>)}</Table.Tbody>
          </Table>
        )}
      </Stack>
      {a.hoai && (a.hoai.applies !== 'nein' || a.hoai.services.length > 0) && <HoaiFindings hoai={a.hoai} />}
      {a.questions.length > 0 && (
        <Stack gap={2}>
          <Text size="sm" fw={600}>Rückfragen an die ausschreibende Stelle ({a.questions.length})</Text>
          <List size="sm" spacing={2} type="ordered">{a.questions.map((q, i) => <List.Item key={i}>{q.urgent && <Badge size="xs" color="red" variant="light" mr={4}>fristrelevant</Badge>}{q.text}<Source source={q.source} /></List.Item>)}</List>
        </Stack>
      )}
      <Group gap="xs">
        <Button size="xs" variant="light" onClick={() => apply.mutate()} loading={apply.isPending}>Angaben ins Angebot übernehmen</Button>
        <Text size="xs" c="dimmed">nur in noch leere Felder: Auftraggeber, Ort, Bundesland, Los, Fristen, Bezug, Anschrift; die Rückfragen, solange noch keine erfasst sind; verlangt die Auslobung den Ausweis der Stunden, wird er in allen Varianten eingeschaltet{offer.feeBasis !== 'hours' && a.hoai?.services.length ? '; die HOAI-Vorschläge als neue Ermittlungen im Reiter HOAI (bestehende bleiben)' : ''}</Text>
      </Group>
      {apply.isError && <Alert color="red" py={6}>{errorText(apply.error, 'Übernehmen fehlgeschlagen.')}</Alert>}
    </Stack>
  );
}

/** What the evaluation found for a fee after the HOAI: per Leistungsbild, every value with its source. */
function HoaiFindings({ hoai }: { hoai: NonNullable<OfferAnalysis['hoai']> }): JSX.Element {
  const known = (k: string): boolean => (HOAI_SERVICES as readonly string[]).includes(k);
  return (
    <Stack gap={2}>
      <Text size="sm" fw={600}>Honorar nach HOAI: {HOAI_APPLIES[hoai.applies]}</Text>
      {hoai.note && <Text size="sm">{hoai.note}</Text>}
      {hoai.services.length > 0 && (
        <List size="sm" spacing={4}>
          {hoai.services.map((h, i) => {
            const def = known(h.service) ? hoaiService(h.service as HoaiService) : null;
            return (
              <List.Item key={i}>
                <Mark mark={h.mark} /><b>{h.label || def?.name || h.service}</b>{def ? ` (${def.name})` : ' – in PST nicht als Leistungsbild abgebildet'}
                <Text size="xs">
                  {def && h.basis !== null ? `${def.basisLabel}: ${formatAmount(h.basis, def.basis === 'costs' ? 0 : 2)}` : 'Bezugsgröße nicht genannt'}
                  {h.basisNote ? ` – ${h.basisNote}` : ''}
                </Text>
                <Text size="xs">
                  {h.phases.length ? `Leistungsphasen ${h.phases.join(', ')}` : 'Leistungsphasen offen'}
                  {h.zone ? ` · Honorarzone ${ROMAN_ZONES[h.zone] ?? h.zone}` : ''}{h.zoneReason ? ` – ${h.zoneReason}` : ''}
                  {h.conversion !== null ? ` · Umbauzuschlag ${h.conversion} %` : ''}{h.incidentals !== null ? ` · Nebenkosten ${h.incidentals} %` : ''}
                </Text>
                <Source source={h.source} />
              </List.Item>
            );
          })}
        </List>
      )}
    </Stack>
  );
}

function StartDraft({ offer, basedOn, disabled, onStarted }: { offer: Offer; basedOn: OfferAiRun; disabled: boolean; onStarted: () => Promise<void> }): JSX.Element {
  const [variantId, setVariantId] = useState(offer.variants[0]!.id);
  const [note, setNote] = useState('');
  const variant = offer.variants.find((v) => v.id === variantId) ?? offer.variants[0]!;
  const start = useMutation({ mutationFn: () => offerAiApi.draft(offer.id, { basedOn: basedOn.id, variantId: variant.id, note }), onSuccess: onStarted });
  const unnamed = variant.data.roles.some((r) => !r.name.trim());
  return (
    <Stack gap={6}>
      <Group align="flex-end" gap="xs">
        <Select size="xs" label="für Variante" w={280} value={variant.id} onChange={(v) => v && setVariantId(v)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
          data={offer.variants.map((v) => ({ value: v.id, label: `${v.code} · ${v.name} (${CONSTELLATION_LABELS[v.constellation]})` }))} />
        <Text size="xs" c="dimmed" pb={6}>Rollen: {variant.data.roles.map((r) => `${r.short} ${r.name || '(ohne Bezeichnung)'}`).join(', ')}</Text>
      </Group>
      <Textarea size="xs" label="Freigabe und Entscheidungen des Büros" description="Was an der Auswertung gilt oder nicht, welcher Weg gewählt ist, was bewusst knapp kalkuliert wird. Das geht der Auswertung vor." autosize minRows={2} maxRows={10}
        value={note} onChange={(e) => setNote(e.currentTarget.value)} placeholder="z. B. Auswertung geprüft. Einzelbieter. Position 3 ohne Vermessung; zwei Abstimmungsrunden vor Ort." />
      {unnamed && <Alert color="orange" py={6}>Eine Rolle der Variante hat noch keine Bezeichnung – erst unter „Kalkulation“ die Rollen festlegen.</Alert>}
      <Group gap="xs">
        <Button onClick={() => start.mutate()} loading={start.isPending} disabled={disabled || unnamed}>Auswertung freigeben und Entwurf anfordern</Button>
        <Text size="xs" c="dimmed">auf Grundlage der Auswertung vom {formatDateTime(basedOn.createdAt)}</Text>
      </Group>
      {start.isError && <Alert color="red" py={6}>{errorText(start.error, 'Der Auftrag ließ sich nicht ablegen.')}</Alert>}
    </Stack>
  );
}

function DraftView({ offer, run, onApplied }: { offer: Offer; run: OfferAiRun; onApplied: () => Promise<void> }): JSX.Element {
  const d = run.answer as OfferDraft;
  const variant = offer.variants.find((v) => v.id === run.variantId);
  const [dropped, setDropped] = useState<string[] | null>(null);
  const apply = useMutation({ mutationFn: () => offerAiApi.apply(offer.id, run.id, 'entwurf'), onSuccess: async (r) => { setDropped(r.dropped); await onApplied(); } });
  const roles = variant?.data.roles.map((r) => r.short) ?? [];
  const h = (line: { hours: { role: string; hours: number }[] }, role: string): string => {
    const sum = line.hours.filter((x) => x.role.toLowerCase() === role.toLowerCase()).reduce((s, x) => s + x.hours, 0);
    return sum ? String(sum) : '';
  };
  const t = d.texts;
  return (
    <Stack gap="sm">
      <Table verticalSpacing={3} withTableBorder>
        <Table.Thead>
          <Table.Tr><Table.Th>Position</Table.Th>{roles.map((r) => <Table.Th key={r} w={50} ta="right">{r} h</Table.Th>)}<Table.Th>Begründung [A]</Table.Th></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {d.positions.map((p, i) => (
            <Table.Tr key={`p${i}`}>
              <Table.Td><Text size="sm" fw={500}>{i + 1}. {p.title}</Text><Text size="xs" c="dimmed" lineClamp={3} title={`Geschuldet: ${p.owed}\nNicht enthalten: ${p.excluded}`}>Geschuldet: {p.owed}</Text></Table.Td>
              {roles.map((r) => <Table.Td key={r} ta="right"><Text size="sm">{h(p, r)}</Text></Table.Td>)}
              <Table.Td><Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{p.reasoning}</Text></Table.Td>
            </Table.Tr>
          ))}
          {d.extras.length > 0 && <Table.Tr><Table.Td colSpan={roles.length + 2}><Text size="sm" fw={600}>Zusatzleistungen</Text></Table.Td></Table.Tr>}
          {d.extras.map((e, i) => (
            <Table.Tr key={`e${i}`}>
              <Table.Td><Text size="sm">Z{i + 1}. {e.title} <Text span size="xs" c="dimmed">({e.unit}, {e.origin})</Text></Text></Table.Td>
              {roles.map((r) => <Table.Td key={r} ta="right"><Text size="sm">{h(e, r)}</Text></Table.Td>)}
              <Table.Td><Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{e.reasoning}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs">
        <b>Texte:</b> Leistungsgegenstand „{t.subject}“ · {t.delimitations.length} Abgrenzungen · {t.assumptions.length} Annahmen · {t.cooperation.length} Mitwirkungspflichten · {t.questions.length} Rückfragen · {t.risks.length} Risiken
        {d.cutNames.some((n) => n) ? ` · Zuschnitte: ${d.cutNames.join(', ')}` : ''}
      </Text>
      {d.notes.length > 0 && (
        <Alert color="yellow" py={6} title="Offene Punkte und wichtigste Annahmen">
          <List size="xs" spacing={0}>{d.notes.map((n, i) => <List.Item key={i}>{n}</List.Item>)}</List>
        </Alert>
      )}
      {variant ? (
        <Group gap="xs">
          <Button size="xs" onClick={() => { if (window.confirm(`Positionen und Zusatzleistungen von Variante ${variant.code} durch den Entwurf ersetzen und die Texte des Angebots übernehmen? Rollen, Sätze und Nachunternehmer bleiben.`)) apply.mutate(); }} loading={apply.isPending}>
            In Variante {variant.code} übernehmen
          </Button>
          <Text size="xs" c="dimmed">ersetzt deren Positionen und Zusatzleistungen; Texte, wo der Entwurf welche hat. Danach unter „Kalkulation“ und „Angaben und Texte“ prüfen.</Text>
        </Group>
      ) : <Text size="xs" c="orange.8">Die Variante dieses Entwurfs gibt es nicht mehr.</Text>}
      {apply.isError && <Alert color="red" py={6}>{errorText(apply.error, 'Übernehmen fehlgeschlagen.')}</Alert>}
      {dropped && dropped.length > 0 && <Alert color="orange" py={6} title="Nicht übernommen">{dropped.map((x) => <Text key={x} size="xs">{x}</Text>)}</Alert>}
    </Stack>
  );
}

function EarlierRuns({ offerId, runs, onRemoved }: { offerId: string; runs: OfferAiRun[]; onRemoved: () => Promise<void> }): JSX.Element {
  return (
    <Section title="Frühere Aufträge" description="Nur das jeweils neueste Ergebnis wird oben gezeigt.">
      {runs.map((r) => (
        <Group key={r.id} gap="xs" wrap="nowrap">
          <Text size="sm" w={90}>{AI_STATIONS[r.station]}</Text>
          <RunHead run={r} offerId={offerId} onRemoved={onRemoved} />
        </Group>
      ))}
    </Section>
  );
}
